import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, ButtonBase, IconButton, useTheme } from '@mui/material';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { useReducedMotion } from 'framer-motion';

import { AtmosphericGlow } from '@nexus/supabase';

import {
  AccountAvatar,
  getInitials,
  resolveAvatarSrc,
  useProfilePicture,
  createMicrosoftPhotoResolver,
  HouseIcon,
  zIndex,
  pressSx,
  motion as motionTokens,
} from '@nexus/ari-lib';
import { Mic, Building2 } from 'lucide-react';

import ariCloudLogo from '../assets/ari-cloud-logo.svg';

import { useStore } from '../lib/store';
import { useAuth } from '../hooks/use-auth';
import { AccountMenu } from './account-menu';
import { fetchGraphToken } from '../lib/fetch-graph-token';
import { RouteTransition } from './route-transition';
import { getAriUrl, isDev } from '../config/global';
import { useAriBusinessUrl } from '../hooks/use-ari-business-url';
import { useWorkspaces } from '../hooks/use-workspaces';
import { ARI_BUSINESS_BILLING_PATH } from '../lib/ari-business-url';

// Module scope: fetchGraphToken is a stable singleton, so the resolver is built once.
const resolveMicrosoftPhoto = createMicrosoftPhotoResolver(fetchGraphToken);

// ----------------------------------------------------------------------

type TabItem = {
  label: string;
  path: string;
};

// Built per render so the dev-only Advanced tab can be appended via conditional
// spread. Labels stay hardcoded to match the other tabs (no i18n).
export function buildTabItems(): TabItem[] {
  return [
    { label: 'Devices', path: '/devices' },
    { label: 'Connectors', path: '/connectors' },
    { label: 'Skills', path: '/skills' },
    { label: 'Memory', path: '/memory' },
    { label: 'Chats', path: '/chats' },
    { label: 'Media', path: '/media' },
    { label: 'Documents', path: '/documents' },
    ...(isDev() ? [{ label: 'Advanced', path: '/advanced' }] : []),
  ];
}

// ----------------------------------------------------------------------

interface CloudLayoutProps {
  children: React.ReactNode;
}

export function CloudLayout({ children }: CloudLayoutProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const supabaseUser = useStore((s) => s.supabaseUser);
  const pictureSrc = useProfilePicture(supabaseUser, resolveMicrosoftPhoto);
  const isBusiness = useStore((s) => s.accountType) === 'business';
  const { signOut } = useAuth();
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const ariBusinessUrl = useAriBusinessUrl();
  const { crtWorkspaceData, error: workspacesError, isLoading: workspacesLoading } = useWorkspaces();
  // CLOUD-684: billing owners only, and never while the workspace is unresolved.
  const canUpgrade =
    !workspacesError &&
    !workspacesLoading &&
    Boolean(crtWorkspaceData?.isBillingOwner) &&
    crtWorkspaceData?.subscriptionPlan !== 'ultra';

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const tabItems = buildTabItems();

  const isActive = (path: string) => (path === '/' ? location.pathname === '/' : location.pathname.startsWith(path));

  return (
    <Box
      data-testid="cloud.layout.container"
      sx={{
        minHeight: '100dvh',
        bgcolor: theme.palette.grey[950],
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Atmospheric glow — same blue-toned glow as the auth screen. The bright
          ellipse rises from the bottom (most of it below the viewport). */}
      <AtmosphericGlow zIndex={zIndex.base} />

      {/* Header */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, sm: 4, md: 6 },
          pt: { xs: 2, sm: 3 },
        }}
      >
        {/* Plain img keeps the width and height attributes for the aspect ratio. */}
        <img
          data-testid="cloud.layout.logo"
          src={ariCloudLogo}
          alt="Ari Cloud"
          width={128}
          height={23}
          style={{ height: 23, width: 'auto', filter: 'brightness(0) invert(1)' }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isBusiness ? (
            // Business accounts manage their plan on the Ari Business portal, so
            // the gradient "Upgrade plan" CTA is replaced by an organization link.
            <Button
              data-testid="cloud.layout.business-btn"
              onClick={() => window.open(ariBusinessUrl(), '_blank', 'noopener,noreferrer')}
              startIcon={<Building2 size={16} />}
              sx={{
                height: 36,
                px: 1,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1,
                minHeight: 0,
                textTransform: 'none',
                color: 'common.white',
                bgcolor: 'transparent',
                '&:hover': { bgcolor: 'transparent', opacity: 0.75 },
              }}
            >
              RealWear
            </Button>
          ) : (
            canUpgrade && (
              <Button
                data-testid="cloud.layout.upgrade-btn"
                component="a"
                href={ariBusinessUrl(ARI_BUSINESS_BILLING_PATH)}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                color="primary"
                sx={(theme) => ({
                  height: { xs: 30, sm: 36 },
                  px: { xs: 1.25, sm: 2 },
                  borderRadius: '50px',
                  fontSize: { xs: 12, sm: 14 },
                  fontWeight: 600,
                  lineHeight: 1,
                  minHeight: 0,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  color: 'common.white',
                  boxShadow: 'none',
                  border: '1.5px solid transparent',
                  background: `linear-gradient(${theme.palette.common.black}, ${theme.palette.common.black}) padding-box,
                               ${theme.gradients.primary.background} border-box`,
                  '&::before': { display: 'none' },
                  '&:hover': {
                    boxShadow: 'none',
                    background: `linear-gradient(${theme.palette.common.black}, ${theme.palette.common.black}) padding-box,
                                 ${theme.gradients.primary.hover} border-box`,
                  },
                })}
              >
                Upgrade plan
              </Button>
            )
          )}

          {/* Ari app button */}
          <ButtonBase
            aria-label={t('accessibility.openAri')}
            onClick={() => window.open(getAriUrl(), '_blank')}
            sx={{
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.grey[100],
              bgcolor: theme.palette.grey[800],
              '&:hover': { bgcolor: theme.palette.grey[700] },
              // Extend the 36px visual to a 40px touch target.
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: 40,
                height: 40,
                transform: 'translate(-50%, -50%)',
              },
              '&.Mui-focusVisible': {
                outline: `2px solid ${theme.palette.primary.light}`,
                outlineOffset: 2,
              },
              ...pressSx(reduceMotion),
              transition: `background-color ${motionTokens.duration.fast} ${motionTokens.ease.out}, transform ${motionTokens.duration.instant} ${motionTokens.ease.out}`,
            }}
          >
            <Mic size={17} color="white" strokeWidth={1.75} />
          </ButtonBase>

          <IconButton
            aria-label={t('accessibility.accountMenu')}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            data-testid="cloud.layout.avatar"
            sx={{ p: 0 }}
          >
            <AccountAvatar
              src={resolveAvatarSrc(pictureSrc ?? undefined)}
              alt={supabaseUser?.name ?? supabaseUser?.email ?? 'User'}
            >
              {getInitials(supabaseUser?.name, supabaseUser?.email ?? 'User')}
            </AccountAvatar>
          </IconButton>
        </Box>

        <AccountMenu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          onAccountClick={() => navigate({ to: '/account' })}
          onSignOutClick={signOut}
        />
      </Box>

      {/* Navigation */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
          pt: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 4 },
          px: { xs: 2, sm: 0 },
        }}
      >
        {/* Home button */}
        <ButtonBase
          aria-label={t('accessibility.home')}
          onClick={() => navigate({ to: '/' })}
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.grey[100],
            bgcolor: theme.palette.grey[800],
            '&:hover': { bgcolor: theme.palette.grey[700] },
            // Extend the 36px visual to a 40px touch target.
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: 40,
              height: 40,
              transform: 'translate(-50%, -50%)',
            },
            '&.Mui-focusVisible': {
              outline: `2px solid ${theme.palette.primary.light}`,
              outlineOffset: 2,
            },
            ...pressSx(reduceMotion),
            transition: `background-color ${motionTokens.duration.fast} ${motionTokens.ease.out}, transform ${motionTokens.duration.instant} ${motionTokens.ease.out}`,
          }}
        >
          <HouseIcon sx={{ width: 17, height: 18 }} />
        </ButtonBase>

        {/* Text tabs — horizontally scrollable on mobile */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: 'rgba(255, 255, 255, 0.06)',
            borderRadius: '10px',
            height: 36,
            px: 0.375,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {tabItems.map((item) => {
            const active = isActive(item.path);

            return (
              <ButtonBase
                key={item.path}
                data-testid={`cloud.nav.${item.path.replace('/', '')}.tab`}
                onClick={() => navigate({ to: item.path })}
                sx={{
                  height: 30,
                  px: 1.5,
                  // Concentric with the 10px container minus its ~3px inset.
                  borderRadius: '7px',
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  color: active ? theme.palette.common.white : theme.palette.grey[400],
                  fontSize: 14,
                  fontWeight: 700,
                  ...(active && {
                    bgcolor: theme.palette.grey[800],
                  }),
                  '&:hover': {
                    bgcolor: active ? theme.palette.grey[800] : 'rgba(255, 255, 255, 0.04)',
                  },
                  '&.Mui-focusVisible': {
                    outline: `2px solid ${theme.palette.primary.light}`,
                    outlineOffset: 1,
                  },
                  ...pressSx(reduceMotion),
                  transition: `background-color ${motionTokens.duration.fast} ${motionTokens.ease.out}, color ${motionTokens.duration.fast} ${motionTokens.ease.out}, transform ${motionTokens.duration.instant} ${motionTokens.ease.out}`,
                }}
              >
                {item.label}
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      {/* Content */}
      <Box
        component="main"
        sx={{
          position: 'relative',
          zIndex: 1,
          px: { xs: 2, sm: 3, md: 5, lg: 10 },
          pb: { xs: 3, md: 5, lg: 6 },
          maxWidth: 1200,
          mx: 'auto',
        }}
      >
        <RouteTransition>{children}</RouteTransition>
      </Box>
    </Box>
  );
}
